"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HomeDashboard = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
var react_2 = require("@heroui/react");
var react_3 = require("@iconify/react");
var dashboardFilters_1 = require("./dashboardFilters");
var kpiCard_1 = require("../charts/kpiCard");
var userEngagementSection_1 = require("./sections/userEngagementSection");
var revenueSection_1 = require("./sections/revenueSection");
var challengeSection_1 = require("./sections/challengeSection");
var gamificationSection_1 = require("./sections/gamificationSection");
var dashboardGrid_1 = require("./dashboardGrid");
var formatChartData_1 = require("../../helpers/analytics/formatChartData");
var compareTimePeriods_1 = require("../../helpers/analytics/compareTimePeriods");
var datePresets_1 = require("../../helpers/analytics/datePresets");
var analytics_1 = require("../../api/analytics");
var KPI_WIDGETS = [
    {
        i: "dau",
        title: "DAU",
        subtitle: "this period",
        icon: "solar:users-group-rounded-linear",
        tooltip: "Distinct users active on any day of the selected period. Activity counts logins, earns, transactions, and challenge interactions.",
    },
    {
        i: "mau",
        title: "MAU",
        subtitle: "this period",
        icon: "solar:chart-2-linear",
        tooltip: "Distinct users active in the trailing 30 days ending on the period's last day.",
    },
    {
        i: "revenue",
        title: "Rewards Issued",
        subtitle: "loyalty paid out this period",
        icon: "solar:hand-money-linear",
        tooltip: "Total loyalty currency paid out (Earn + Cashback + Reversal) in the selected period.",
    },
    {
        i: "challenges",
        title: "Challenge Completion",
        subtitle: "of started challenges this period",
        icon: "solar:target-linear",
        tooltip: "Share of started challenges in the period that reached completed status.",
    },
    {
        i: "active_rate",
        title: "Active Rate",
        subtitle: "this period",
        icon: "solar:users-group-rounded-linear",
        tooltip: "Share of registered members with any activity in the selected period.",
    },
    {
        i: "redemption",
        title: "Milestone Rate",
        subtitle: "of active users this period",
        icon: "solar:gift-linear",
        tooltip: "Share of active users who hit at least one reward-track milestone in the period. Denominator is active users, not total members.",
    },
];
var HomeDashboard = function () {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    var _r = (0, react_1.useState)({
        date_range: { preset: "30d" },
        granularity: (0, dashboardFilters_1.autoGranularity)(30),
    }), filterState = _r[0], setFilterState = _r[1];
    var _s = (0, react_1.useState)(new Date()), lastUpdated = _s[0], setLastUpdated = _s[1];
    var dateRange = (0, react_1.useMemo)(function () { return (0, datePresets_1.calculateDateRange)(filterState.date_range); }, [filterState.date_range]);
    var granularity = filterState.granularity;
    // the five headline endpoints are asked twice: once without comparison deltas so charts paint, then again with them.
    // separate cache keys mean the enriched answer no longer overwrites the fast one.
    var fast = __assign(__assign({}, dateRange), { compare: false });
    var enriched = __assign(__assign({}, dateRange), { compare: true });
    var overviewFast = (0, analytics_1.useGetDashboardOverviewQuery)(fast);
    var overviewEnriched = (0, analytics_1.useGetDashboardOverviewQuery)(enriched);
    var engagementFast = (0, analytics_1.useGetUserEngagementQuery)(__assign(__assign({}, fast), { granularity: granularity }));
    var engagementEnriched = (0, analytics_1.useGetUserEngagementQuery)(__assign(__assign({}, enriched), { granularity: granularity }));
    var revenueFast = (0, analytics_1.useGetRevenueAnalyticsQuery)(__assign(__assign({}, fast), { granularity: granularity }));
    var revenueEnriched = (0, analytics_1.useGetRevenueAnalyticsQuery)(__assign(__assign({}, enriched), { granularity: granularity }));
    var challengesFast = (0, analytics_1.useGetChallengeAnalyticsQuery)(__assign(__assign({}, fast), { granularity: granularity, top: 10 }));
    var challengesEnriched = (0, analytics_1.useGetChallengeAnalyticsQuery)(__assign(__assign({}, enriched), { granularity: granularity, top: 10 }));
    var gamificationFast = (0, analytics_1.useGetGamificationAnalyticsQuery)(__assign(__assign({}, fast), { granularity: granularity, type: "all" }));
    var gamificationEnriched = (0, analytics_1.useGetGamificationAnalyticsQuery)(__assign(__assign({}, enriched), { granularity: granularity, type: "all" }));
    var tierDistributionQuery = (0, analytics_1.useGetTierDistributionQuery)({}, { skip: !userEngagementSection_1.TIER_DISTRIBUTION_ENABLED });
    var activityHeatmapQuery = (0, analytics_1.useGetActivityHeatmapQuery)(dateRange);
    var topRewardsQuery = (0, analytics_1.useGetTopRewardsQuery)(__assign(__assign({}, dateRange), { top: 10 }));
    var topBranchesQuery = (0, analytics_1.useGetTopBranchesQuery)(__assign(__assign({}, dateRange), { top: 8 }));
    var topEarnersQuery = (0, analytics_1.useGetTopEarnersQuery)(__assign(__assign({}, dateRange), { top: 10 }));
    var dashboardOverview = (_b = (_a = overviewEnriched.data) !== null && _a !== void 0 ? _a : overviewFast.data) !== null && _b !== void 0 ? _b : null;
    var userEngagement = (_d = (_c = engagementEnriched.data) !== null && _c !== void 0 ? _c : engagementFast.data) !== null && _d !== void 0 ? _d : null;
    var revenueAnalytics = (_f = (_e = revenueEnriched.data) !== null && _e !== void 0 ? _e : revenueFast.data) !== null && _f !== void 0 ? _f : null;
    var challengeAnalytics = (_h = (_g = challengesEnriched.data) !== null && _g !== void 0 ? _g : challengesFast.data) !== null && _h !== void 0 ? _h : null;
    var gamificationAnalytics = (_k = (_j = gamificationEnriched.data) !== null && _j !== void 0 ? _j : gamificationFast.data) !== null && _k !== void 0 ? _k : null;
    var tierDistribution = (_l = tierDistributionQuery.data) !== null && _l !== void 0 ? _l : null;
    var activityHeatmap = (_m = activityHeatmapQuery.data) !== null && _m !== void 0 ? _m : null;
    var topRewards = (_o = topRewardsQuery.data) !== null && _o !== void 0 ? _o : null;
    var topBranches = (_p = topBranchesQuery.data) !== null && _p !== void 0 ? _p : null;
    var topEarners = (_q = topEarnersQuery.data) !== null && _q !== void 0 ? _q : null;
    // isLoading already means "no cached answer for this range yet", which is what the sections want for a skeleton
    var loading = {
        overview: overviewFast.isLoading,
        engagement: engagementFast.isLoading,
        revenue: revenueFast.isLoading,
        challenges: challengesFast.isLoading,
        gamification: gamificationFast.isLoading,
        tier_distribution: tierDistributionQuery.isLoading,
        activity_heatmap: activityHeatmapQuery.isLoading,
        top_rewards: topRewardsQuery.isLoading,
        top_branches: topBranchesQuery.isLoading,
        top_earners: topEarnersQuery.isLoading,
    };
    var allQueries = [
        overviewFast,
        overviewEnriched,
        engagementFast,
        engagementEnriched,
        revenueFast,
        revenueEnriched,
        challengesFast,
        challengesEnriched,
        gamificationFast,
        gamificationEnriched,
        tierDistributionQuery,
        activityHeatmapQuery,
        topRewardsQuery,
        topBranchesQuery,
        topEarnersQuery,
    ];
    var anyFetchInFlight = allQueries.some(function (query) { return query.isFetching; });
    var refreshAll = function () {
        allQueries.forEach(function (query) { return query.refetch(); });
        setLastUpdated(new Date());
    };
    var REFRESH_COOLDOWN_MS = 3000;
    var refreshDisabled = anyFetchInFlight || Date.now() - lastUpdated.getTime() < REFRESH_COOLDOWN_MS;
    var rangeLabel = (0, react_1.useMemo)(function () {
        var range = (0, datePresets_1.calculateDateRange)(filterState.date_range);
        var formatDay = function (value) { return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
        return "".concat(formatDay(range.start_date), " \u2013 ").concat(formatDay(range.end_date));
    }, [filterState.date_range]);
    var lastUpdatedLabel = (0, react_1.useMemo)(function () {
        var seconds = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
        if (seconds < 60)
            return "just now";
        if (seconds < 3600)
            return "".concat(Math.floor(seconds / 60), " min ago");
        if (seconds < 86400)
            return "".concat(Math.floor(seconds / 3600), " h ago");
        return lastUpdated.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }, [lastUpdated]);
    (0, react_1.useEffect)(function () {
        setLastUpdated(new Date());
    }, [dateRange, granularity]);
    var handleFilterChange = (0, react_1.useCallback)(function (filters) {
        setFilterState(filters);
    }, []);
    var sparklines = (0, react_1.useMemo)(function () {
        var toSeries = function (points) { return (points && points.length > 1 ? points.map(function (point) { return ({ value: point.value }); }) : undefined); };
        return {
            dau: toSeries(userEngagement === null || userEngagement === void 0 ? void 0 : userEngagement.dau_time_series),
            mau: toSeries(userEngagement === null || userEngagement === void 0 ? void 0 : userEngagement.mau_time_series),
            revenue: toSeries(revenueAnalytics === null || revenueAnalytics === void 0 ? void 0 : revenueAnalytics.time_series),
            challenges: toSeries(challengeAnalytics === null || challengeAnalytics === void 0 ? void 0 : challengeAnalytics.completion_time_series),
        };
    }, [userEngagement, revenueAnalytics, challengeAnalytics]);
    var kpiWidgets = (0, react_1.useMemo)(function () {
        var kpi = dashboardOverview === null || dashboardOverview === void 0 ? void 0 : dashboardOverview.kpi_metrics;
        if (!kpi) {
            return KPI_WIDGETS.map(function (meta) { return ({
                i: meta.i,
                title: meta.title,
                subtitle: meta.subtitle,
                value: "—",
                icon: meta.icon,
                trend: undefined,
                sparkline: [],
                tooltip: meta.tooltip,
            }); });
        }
        return KPI_WIDGETS.map(function (meta) {
            var value = 0;
            var trend = undefined;
            var subtitle = meta.subtitle;
            var sparkline;
            switch (meta.i) {
                case "dau":
                    value = (0, formatChartData_1.formatCompactNumber)(kpi.dau);
                    trend = (0, compareTimePeriods_1.formatComparison)(kpi.dau_comparison);
                    sparkline = sparklines.dau;
                    break;
                case "mau":
                    value = (0, formatChartData_1.formatCompactNumber)(kpi.mau);
                    trend = (0, compareTimePeriods_1.formatComparison)(kpi.mau_comparison);
                    sparkline = sparklines.mau;
                    break;
                case "revenue":
                    value = (0, formatChartData_1.formatCurrency)(kpi.total_revenue, dashboardOverview === null || dashboardOverview === void 0 ? void 0 : dashboardOverview.currency);
                    trend = (0, compareTimePeriods_1.formatComparison)(kpi.revenue_comparison);
                    sparkline = sparklines.revenue;
                    break;
                case "challenges":
                    value = (0, formatChartData_1.formatPercentage)(kpi.challenge_completion_rate);
                    trend = (0, compareTimePeriods_1.formatComparison)(kpi.completion_comparison);
                    sparkline = sparklines.challenges;
                    break;
                case "active_rate":
                    value = (0, formatChartData_1.formatPercentage)(kpi.active_member_rate);
                    trend = (0, compareTimePeriods_1.formatComparison)(kpi.active_rate_comparison);
                    sparkline = [];
                    break;
                case "redemption":
                    value = (0, formatChartData_1.formatPercentage)(kpi.redemption_rate);
                    trend = (0, compareTimePeriods_1.formatComparison)(kpi.redemption_comparison);
                    sparkline = [];
                    break;
            }
            return { i: meta.i, title: meta.title, subtitle: subtitle, value: value, icon: meta.icon, trend: trend, sparkline: sparkline !== null && sparkline !== void 0 ? sparkline : [], tooltip: meta.tooltip };
        });
    }, [dashboardOverview, challengeAnalytics, sparklines]);
    var kpiGridWidgets = kpiWidgets.map(function (widget, idx) { return ({
        i: widget.i,
        x: (idx % 6) * 2,
        y: 0,
        w: 2,
        h: 2,
        hideTitle: true,
        noShell: true,
        content: ((0, jsx_runtime_1.jsx)(kpiCard_1.KPICard, { title: widget.title, value: widget.value, icon: widget.icon, subtitle: widget.subtitle, trend: widget.trend, sparkline: widget.sparkline, tooltip: widget.tooltip, loading: loading.overview && !dashboardOverview })),
    }); });
    return ((0, jsx_runtime_1.jsxs)("div", { className: "flex flex-col gap-6", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex flex-wrap items-end justify-between gap-4", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex flex-col gap-0.5", children: [(0, jsx_runtime_1.jsx)("h1", { className: "text-2xl font-bold text-foreground", children: "Dashboard" }), (0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-2 text-xs text-muted", children: [(0, jsx_runtime_1.jsx)("span", { children: rangeLabel }), (0, jsx_runtime_1.jsx)("span", { "aria-hidden": true, children: "\u00B7" }), (0, jsx_runtime_1.jsxs)("span", { children: ["Updated ", lastUpdatedLabel] })] })] }), (0, jsx_runtime_1.jsxs)("div", { className: "flex flex-wrap items-center gap-2 rounded-lg bg-surface px-2 py-1.5 shadow-sm", children: [(0, jsx_runtime_1.jsx)(dashboardFilters_1.DashboardFilters, { onFilterChange: handleFilterChange }), (0, jsx_runtime_1.jsx)("span", { className: "mx-1 h-5 w-px bg-border", "aria-hidden": true }), (0, jsx_runtime_1.jsxs)(react_2.Tooltip, { delay: 0, children: [(0, jsx_runtime_1.jsx)(react_2.Tooltip.Trigger, { children: (0, jsx_runtime_1.jsx)(react_2.Button, { size: "sm", variant: "tertiary", isIconOnly: true, onPress: refreshAll, isPending: anyFetchInFlight, isDisabled: refreshDisabled, children: (0, jsx_runtime_1.jsx)(react_3.Icon, { icon: "solar:refresh-linear", className: "text-base" }) }) }), (0, jsx_runtime_1.jsx)(react_2.Tooltip.Content, { children: refreshDisabled ? "Just refreshed, wait a moment" : "Refresh" })] })] })] }), (0, jsx_runtime_1.jsx)(dashboardGrid_1.DashboardGrid, { widgets: kpiGridWidgets }), (0, jsx_runtime_1.jsx)(userEngagementSection_1.UserEngagementSection, { data: userEngagement, loading: loading.engagement && !userEngagement, tier_distribution: tierDistribution, tier_distribution_loading: loading.tier_distribution, activity_heatmap: activityHeatmap, activity_heatmap_loading: loading.activity_heatmap }), (0, jsx_runtime_1.jsx)(revenueSection_1.RevenueSection, { data: revenueAnalytics, loading: loading.revenue && !revenueAnalytics, top_branches: topBranches, top_branches_loading: loading.top_branches, top_earners: topEarners, top_earners_loading: loading.top_earners }), (0, jsx_runtime_1.jsx)(challengeSection_1.ChallengeSection, { data: challengeAnalytics, loading: loading.challenges && !challengeAnalytics }), (0, jsx_runtime_1.jsx)(gamificationSection_1.GamificationSection, { data: gamificationAnalytics, loading: loading.gamification && !gamificationAnalytics, top_rewards: topRewards, top_rewards_loading: loading.top_rewards })] }));
};
exports.HomeDashboard = HomeDashboard;
